import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import tw from "twin.macro";
import SEO from "../components/seo";

import Layout from "../components/layout";
import AppLink from "../components/app-link";
import Corrections from "../components/corrections";

const Container = tw.div`container mx-auto py-4`;
const ContactHeading = tw.div`my-8`;
const ContactName = tw.h1`text-2xl font-bold`;
const ContactTitle = tw.h3`font-bold`;
const Contact = tw.dl`bg-white py-6 px-8 rounded shadow-md max-w-md`;
const ContactItem = tw.dt`font-bold mt-2 uppercase text-xs tracking-wide text-gray-700`;
const ContactValue = tw.dd`mb-4 leading-tight`;
const Address = tw.address`not-italic`;
const AddressLine = tw.div``;

/**
 * Formats phone number for anchor tag
 * @param {string} s - phone number in the format (aaa) bbb-cccc
 * @returns {string} in the format +1aaabbbcccc
 **/
const formatPhoneNumber = s =>
  s.replace(/\D+/g, "").replace(/(\d{3})(\d{3})(\d{4})/, "+1$1$2$3");

const Template = ({ data }) => {
  const { contactsJson } = data;
  const {
    first_name,
    last_name,
    email,
    degree,
    affiliation,
    phone,
    address1,
    address2,
    city,
    state,
    zip,
    title,
    cpctnet_role,
    admin_assistant_last_name,
    admin_assistant_first_name,
    admin_assistant_email
  } = contactsJson;
  return (
    <Layout>
      <SEO title={`${first_name} ${last_name} - Contact Directory`} />
      <Container>
        <AppLink to="/contacts-directory">Back to Contacts Directory</AppLink>
        <ContactHeading>
          <ContactName>
            {first_name} {last_name}
            {degree && `, ${degree}`}
          </ContactName>
          {title && <ContactTitle>{title}</ContactTitle>}
        </ContactHeading>
        <div>
          <Contact>
            {cpctnet_role && (
              <>
                <ContactItem>CP-CTNet Role</ContactItem>
                <ContactValue>{cpctnet_role}</ContactValue>
              </>
            )}
            {affiliation && (
              <>
                <ContactItem>Affiliation</ContactItem>
                <ContactValue>{affiliation}</ContactValue>
              </>
            )}
            {email && (
              <>
                <ContactItem>Email</ContactItem>
                <ContactValue>
                  <AppLink href={`mailto:${email}`}>{email}</AppLink>
                </ContactValue>
              </>
            )}
            {phone && (
              <>
                <ContactItem>Phone</ContactItem>
                <ContactValue>
                  <AppLink href={`tel:${formatPhoneNumber(phone)}`}>
                    {phone}
                  </AppLink>
                </ContactValue>
              </>
            )}
            {(address1 || address2 || city || state || zip) && (
              <>
                <ContactItem>Address</ContactItem>
                <ContactValue>
                  <Address>
                    <AddressLine>{address1}</AddressLine>
                    <AddressLine>{address2}</AddressLine>
                    <AddressLine>
                      {city && `${city}, `}
                      {state} {zip}
                    </AddressLine>
                  </Address>
                </ContactValue>
              </>
            )}
            {(admin_assistant_last_name ||
              admin_assistant_first_name ||
              admin_assistant_email) && (
              <>
                <ContactItem>Administrative Assistant</ContactItem>
                <ContactValue>
                  {admin_assistant_email ? (
                    <AppLink href={`mailto:${admin_assistant_email}`}>
                      {admin_assistant_first_name || admin_assistant_last_name
                        ? `${admin_assistant_first_name} ${admin_assistant_last_name}`
                        : admin_assistant_email}
                    </AppLink>
                  ) : (
                    `${admin_assistant_first_name} ${admin_assistant_last_name}`
                  )}
                </ContactValue>
              </>
            )}
          </Contact>
        </div>
      </Container>
      <Corrections />
    </Layout>
  );
};

Template.propTypes = {
  data: PropTypes.object
};

export default Template;

export const pageQuery = graphql`
  query($id: String!) {
    contactsJson(id: { eq: $id }) {
      first_name
      last_name
      email
      degree
      affiliation
      phone
      title
      cpctnet_role
      address1
      address2
      city
      state
      zip
      admin_assistant_last_name
      admin_assistant_first_name
      admin_assistant_email
    }
  }
`;
